import React from 'react';
import { Box, Typography, Chip, Drawer, IconButton, Paper } from '@mui/material';
import { Business, Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { RATING, fmt } from './constants';
import LeadersTable from './LeadersTable';

const SectorDrawer = ({ expandedIcb, setExpandedIcb, items, leadersCache }) => {
  const { t } = useTranslation();

  const code = expandedIcb;
  const meta = code ? items.find(it => it.icb_code === code) : null;
  const groupName = meta ? (meta.name_vi || meta.name_en) : (code || '');
  const state = code ? leadersCache[code] : null;
  const data = state?.data;
  const ratingC = meta ? (RATING[meta.rating] || RATING.NEUTRAL) : null;

  return (
    <Drawer
      anchor="right"
      open={!!expandedIcb}
      onClose={() => setExpandedIcb(null)}
      PaperProps={{ sx: { width: { xs: '100%', md: '85%', lg: 1180 }, maxWidth: '100vw', bgcolor: '#f8fafc' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Gradient hero header */}
        <Box sx={{
          px: 3, py: 2,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #3b82f6 100%)',
          color: 'white',
          position: 'sticky', top: 0, zIndex: 3,
          boxShadow: '0 2px 12px rgba(30,58,138,0.25)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Business sx={{ fontSize: 18, opacity: 0.9 }} />
                <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600, letterSpacing: 0.5 }}>
                  {t('icb.leaders.drawerLabel')}
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.15, mb: 0.3 }} noWrap>
                {groupName}
              </Typography>
              {meta?.name_en && meta.name_en !== groupName && (
                <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.78rem' }} noWrap>
                  {meta.name_en}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={() => setExpandedIcb(null)}
              size="small"
              aria-label="close"
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Quick stats strip */}
          {meta && (
            <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
              {ratingC && (
                <Chip label={t(`icb.rating.${meta.rating || 'NEUTRAL'}`)} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 700, fontSize: '0.65rem', height: 22 }} />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>{t('icb.leaders.groupRank')}:</Typography>
                <Typography variant="caption" fontWeight={700}>#{meta.rank}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>{t('icb.leaders.groupFlow')}:</Typography>
                <Typography variant="caption" fontWeight={700}>{fmt(meta.foreign_flow_score)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>{t('icb.leaders.groupMomentum')}:</Typography>
                <Typography variant="caption" fontWeight={700}>{fmt(meta.momentum_score)}</Typography>
              </Box>
              {data && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    {t('icb.leaders.metaInfoNoDate', { count: data.leader_count })} · {data.score_date || '-'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }}>
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <LeadersTable state={state} groupName={groupName} hideHeader />
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
            {t('icb.leaders.footerHint')}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SectorDrawer;
